import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import Rails from "@rails/ujs";
import DropdownComponentController from "../dropdown_component_controller";
import { searchPattern } from "./search_pattern";
import { Announcer, countText, highlight, prepareOption } from "./combobox";

const WRAPPER = '[data-controller~="dropdown-component"]';

// Filters a dropdown panel's results. Needs an `input` target with
// data-action="input->dropdown-component--search#filter
// keydown->dropdown-component--search#navigate", and an `item` target with
// data-search="<text>" on each result. SearchFieldComponent renders the
// input as a combobox with a `list` target (the results' listbox) and a
// `status` target (a polite live region); this controller keeps their state:
// aria-expanded, the options' roles and ids, aria-activedescendant and
// aria-selected on the highlighted result, and the result count.

export default class extends Controller {
  static targets = ["input", "item", "list", "status"];

  connect() {
    this.dropdown = this.element.closest(WRAPPER);
    this.open = false;
    this.announcer = new Announcer(
      this.hasStatusTarget ? this.statusTarget : null,
    );
    this.onShown = (event) => {
      if (event.target === this.dropdown) this.shown();
    };
    this.onHidden = (event) => {
      if (event.target === this.dropdown) this.reset();
    };
    this.onFrameLoad = () => this.resultsLoaded();
    this.element.addEventListener("turbo:frame-load", this.onFrameLoad);
    if (this.dropdown) {
      this.dropdown.addEventListener("dropdown-component:shown", this.onShown);
      this.dropdown.addEventListener(
        "dropdown-component:hidden",
        this.onHidden,
      );
    }
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.onFrameLoad);
    if (this.dropdown) {
      this.dropdown.removeEventListener(
        "dropdown-component:shown",
        this.onShown,
      );
      this.dropdown.removeEventListener(
        "dropdown-component:hidden",
        this.onHidden,
      );
    }
    this.dropdown = null;
    this.announcer.clear();
  }

  // Results rendered later (a lazy Turbo frame's) connect here too.
  itemTargetConnected(item) {
    if (this.hasListTarget && this.listTarget.contains(item))
      prepareOption(this.listTarget, item);
    if (this.open) this.announce();
  }

  // Turbo marks a lazy frame complete only after its results have connected,
  // so the count announced then may still be "Loading results".
  resultsLoaded() {
    if (!this.open) return;
    if (this.inputTarget.value === "") this.announce();
    else this.filter();
  }

  shown() {
    this.open = true;
    this.inputTarget.setAttribute("aria-expanded", "true");
    this.inputTarget.focus();
    const visible = this.visibleItems();
    highlight(
      this.inputTarget,
      this.itemTargets,
      visible.find((el) => el.classList.contains("active")) ?? null,
    );
    this.announce();
  }

  filter() {
    const value = this.inputTarget.value;
    const pattern = searchPattern(value);
    this.itemTargets.forEach((item) => {
      const text = item.getAttribute("data-search");
      const match = value === "" || (text !== null && pattern.test(text));
      item.classList.toggle("d-none", !match);
    });
    highlight(this.inputTarget, this.itemTargets, this.visibleItems()[0]);
    this.announce();
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        this.move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        this.move(-1);
        break;
      case "Enter":
        if (event.isComposing) return;
        event.preventDefault();
        this.activate();
        break;
    }
  }

  move(step) {
    const visible = this.visibleItems();
    const index = visible.findIndex((el) => el.classList.contains("active"));
    const next =
      index === -1 ? (step > 0 ? visible[0] : null) : visible[index + step];
    if (next)
      highlight(this.inputTarget, this.itemTargets, next, { scroll: true });
  }

  activate() {
    const results = this.visibleItems().filter((el) =>
      el.classList.contains("active"),
    );
    if (results.length !== 1) return;
    const el = results[0];
    if (el.hasAttribute("data-remote") || el.hasAttribute("data-click")) {
      Rails.fire(el, "click");
    } else {
      DropdownComponentController.for(this.element)?.hide();
      Turbo.visit(el.href);
    }
  }

  reset() {
    this.open = false;
    this.inputTarget.value = "";
    this.inputTarget.blur();
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.itemTargets.forEach((item) => item.classList.remove("d-none"));
    highlight(this.inputTarget, this.itemTargets, null);
    this.announcer.clear();
  }

  // The visible result count, or that results are still loading (a lazy
  // Turbo frame that hasn't completed).
  announce() {
    const scope = this.hasListTarget ? this.listTarget : this.element;
    const loading = scope.querySelector("turbo-frame[src]:not([complete])");
    this.announcer.announce(
      loading ? "Loading results" : countText(this.visibleItems().length),
    );
  }

  visibleItems() {
    return this.itemTargets.filter((el) => !el.classList.contains("d-none"));
  }
}
